import { useMemo } from 'react';
import { useQuery, QueryClient } from 'react-query';
import { useLocationGroupStore } from '@frontend/scope';
import { executeWetagQuery, wetagQueryKeys, getWetagConfig } from './api';
import { normalizeToQuery } from './query-builder';
import type { WetagQueryOptions, WetagResult, WetagQueryResponse, WetagInput } from './types';

const DEFAULT_OPTIONS: Required<WetagQueryOptions> = {
  cacheTime: Infinity,
  staleTime: Infinity,
  defaultValue: false,
  enabled: true,
};

export function useWetag<TTag extends string = string>(
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string | undefined,
  options: WetagQueryOptions = {}
): [boolean, boolean, WetagQueryResponse | undefined] {
  const result = useWetagQuery(namespaceId, input, entityId, options);
  return [result.isEnabled, result.isLoading, result.response];
}

export function useWetagQuery<TTag extends string = string>(
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string | undefined,
  options: WetagQueryOptions = {}
): WetagResult {
  const config = getWetagConfig();
  const hasLocationsSelected = useLocationGroupStore((state) => state.selectedLocationIds.length > 0);

  const query = useMemo(() => normalizeToQuery(input), [input]);

  const mergedOptions = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      ...config.defaultOptions,
      ...options,
    }),
    [config.defaultOptions, options]
  );

  const { cacheTime, staleTime, defaultValue, enabled } = mergedOptions;
  const isQueryEnabled = enabled && !!entityId && hasLocationsSelected;

  const reactQuery = useQuery(
    wetagQueryKeys.query(namespaceId, entityId || '', query),
    () => executeWetagQuery({ namespaceId, query, entityId: entityId }),
    {
      enabled: isQueryEnabled,
      cacheTime,
      staleTime,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );

  const isEnabled = reactQuery.data?.result ?? defaultValue;

  return {
    isEnabled: reactQuery.isError ? defaultValue : isEnabled,
    isLoading: reactQuery.isLoading,
    isError: reactQuery.isError,
    error: reactQuery.error as Error | null,
    isSuccess: reactQuery.isSuccess,
    refetch: reactQuery.refetch,
    response: reactQuery.data,
  };
}

export async function prefetchWetag<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string,
  options: WetagQueryOptions = {}
): Promise<void> {
  const config = getWetagConfig();
  const query = normalizeToQuery(input);

  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...config.defaultOptions,
    ...options,
  };

  const { cacheTime, staleTime } = mergedOptions;

  await queryClient.prefetchQuery(
    wetagQueryKeys.query(namespaceId, entityId, query),
    () => executeWetagQuery({ namespaceId, query, entityId }),
    { cacheTime, staleTime }
  );
}

export function getWetagFromCache<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string
): boolean | undefined {
  const query = normalizeToQuery(input);
  const data = queryClient.getQueryData<WetagQueryResponse>(wetagQueryKeys.query(namespaceId, entityId, query));
  return data?.result;
}

export function invalidateWetag<TTag extends string = string>(
  queryClient: QueryClient,
  namespaceId: string,
  input: WetagInput<TTag>,
  entityId: string
): Promise<void> {
  const query = normalizeToQuery(input);
  return queryClient.invalidateQueries(wetagQueryKeys.query(namespaceId, entityId, query));
}

export function invalidateEntityWetags(queryClient: QueryClient, namespaceId: string, entityId: string): Promise<void> {
  return queryClient.invalidateQueries(wetagQueryKeys.entity(namespaceId, entityId));
}

export function invalidateNamespaceWetags(queryClient: QueryClient, namespaceId: string): Promise<void> {
  return queryClient.invalidateQueries(wetagQueryKeys.namespace(namespaceId));
}

export function invalidateAllWetags(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries(wetagQueryKeys.all);
}
